import { getLevelDescription, getLevelLabel } from '@app/src/pages/Assessments/Assessment/utils'
import type Assessment from '@app/src/types/assessment'
import { AssessmentLevelNamesType, AssessmentType } from '@app/src/types/resourceExplorer'
import { LabelFormat } from '@app/src/utils/assessmentLevelTranslations'
import { percentageToLevelType } from '@app/src/utils/assessmentLevels'
import { useTheme } from '@mui/material/styles'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

export const useAssessmentMetrics = (assessment: Assessment | undefined) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()

  const displayPercentage = useMemo(() => {
    if (!assessment) return 0

    if (assessment.assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment) {
      return (assessment.totalLevel ?? 0) * 20
    }

    const percentage = assessment.percentage ?? 0
    return percentage <= 0 ? 0 : percentage
  }, [assessment?.percentage, assessment?.totalLevel, assessment?.assessmentTemplate?.assessmentType])

  if (!assessment) {
    return {
      getLevelDescription: () => '',
      getCircleColor: () => theme.palette.brandDecorative.jade,
      getCircleValue: () => 0,
      getStatusText: () => '',
      getScoreText: () => '',
      getCircleText: () => '',
      displayPercentage,
    }
  }

  const assessmentType = assessment.assessmentTemplate?.assessmentType ?? AssessmentType.StandardAssessment
  const totalLevels = assessment.assessmentTemplate?.levels ?? 5
  const levelNamesType = assessment.assessmentTemplate?.levelNamesType ?? AssessmentLevelNamesType.Intervals
  const currentLevel = assessment.totalLevel ?? 0
  const percentage = assessment.percentage ?? 0

  const getAssessmentLevelDescription = (level: number) => {
    const companyName = assessment.provider?.name ?? assessment.provider?.organization?.name
    return getLevelDescription(level, assessment.assessmentTemplate, companyName, formatMessage)
  }

  const getCircleColor = () => {
    if (assessmentType === AssessmentType.StandardAssessment) {
      const levelType = percentageToLevelType(
        percentage,
        totalLevels,
        assessment.assessmentTemplate.levelThresholdPercentages,
      )

      switch (levelType) {
        case 'veryPoor':
          return theme.palette.semantic.error
        case 'poor':
          return theme.palette.error.dark
        case 'fair':
          return theme.palette.warning.dark
        case 'good':
          return theme.palette.success.dark
        default:
          return theme.palette.brandDecorative.jade
      }
    }

    if (totalLevels === 3) {
      if (currentLevel === 1) return theme.palette.semantic.error
      if (currentLevel === 2) return theme.palette.warning.dark
      if (currentLevel === 3) return theme.palette.brandDecorative.jade
    } else {
      if (currentLevel === 1) return theme.palette.semantic.error
      if (currentLevel === 2) return theme.palette.error.dark
      if (currentLevel === 3) return theme.palette.warning.dark
      if (currentLevel === 4) return theme.palette.success.dark
      if (currentLevel === 5) return theme.palette.brandDecorative.jade
    }
    return theme.palette.brandDecorative.jade
  }

  const getCircleValue = () => {
    if (assessmentType === AssessmentType.StandardAssessment) {
      return percentage <= 0 ? 5 : percentage
    }
    if (assessmentType === AssessmentType.BaselineAssessment) {
      return currentLevel * 20
    }
    return (currentLevel / totalLevels) * 100
  }

  const getStatusText = () => {
    if (currentLevel > 0) {
      return getLevelLabel(currentLevel, totalLevels, assessmentType, levelNamesType, formatMessage, LabelFormat.Basic)
    }
    return formatMessage({ id: 'general.notAvailable' })
  }

  const getScoreText = () => {
    if (assessmentType === AssessmentType.BaselineAssessment) {
      return formatMessage(
        { id: 'assessment.scoreText.baseline' },
        { status: getStatusText(), score: assessment.totalScore },
      )
    }
    return formatMessage(
      { id: 'assessment.scoreText.standard' },
      { status: getStatusText(), percentage: displayPercentage },
    )
  }

  const getCircleText = () => {
    if (assessmentType !== AssessmentType.StandardAssessment) {
      return formatMessage({ id: 'assessment.levelShort' }, { level: currentLevel })
    }
    return displayPercentage
  }

  return {
    getLevelDescription: getAssessmentLevelDescription,
    getCircleColor,
    getCircleValue,
    getStatusText,
    getScoreText,
    getCircleText,
    displayPercentage,
  }
}
